'use client'

import posthog from 'posthog-js'

type EventParams = Record<string, any>

export const useAnalytics = () => {
  const isClient = typeof window !== 'undefined'
  const currentPath = isClient ? window.location.pathname : ''

  const trackEvent = (eventName: string, eventParams?: EventParams) => {
    // GA
    try {
      if (isClient && typeof window.gtag === 'function') {
        window.gtag('event', eventName, {
          ...eventParams,
          debug_mode: process.env.NODE_ENV !== 'production',
        })
      } else {
        console.warn('gtag not found on window')
      }
    } catch (error) {
      console.error('Failed to send GA event:', error)
    }

    // PostHog
    try {
      posthog.capture(eventName, {
        $current_url: window.location.pathname + window.location.search + window.location.hash,
        ...eventParams,
      })
    } catch (error) {
      if (process.env.NODE_ENV !== 'production')
        console.error('Failed to send PostHog event:', error)
    }
  }

  // ── Identity (PostHog only — GA4 không có identify API) ──────
  const identifyUser = (userId: string, traits: EventParams = {}) => {
    try {
      posthog.identify(userId, Object.keys(traits).length ? traits : undefined)
    } catch {}
  }

  const resetUser = () => {
    try {
      posthog.reset()
    } catch {}
  }

  // === PAGE VIEW ===
  const trackPageView = (pagePath: string, title?: string) => {
    trackEvent('page_view', {
      page_path: pagePath,
      page_title: title || document.title,
    })
  }

  // === HOMEPAGE EVENTS ===
  const trackHomepageBannerClick = (bannerName: string) => {
    trackEvent('banner_click', {
      section: 'hero',
      banner_name: bannerName,
    })
  }

  const trackCTAClick = (location: string, buttonText: string) => {
    trackEvent('booking_click', {
      location,
      button_text: buttonText,
    })
  }

  // === HOTEL PAGE EVENTS ===
  const trackHotelView = (hotelName: string) => {
    trackEvent('view_hotel', {
      hotel_name: hotelName,
    })
  }

  const trackRoomView = (hotelName: string, roomType: string, price?: number) => {
    trackEvent('view_room', {
      hotel_name: hotelName,
      room_type: roomType,
      price,
    })
  }

  const trackBookingClick = (hotelName: string, roomType?: string, position?: string) => {
    trackEvent('select_room', {
      hotel_name: hotelName,
      room_type: roomType || 'not_specified',
      position: position || 'unknown',
      source: currentPath,
    })
  }

  const trackStartCheckout = (hotelName: string, step: number, roomType: string) => {
    trackEvent('begin_checkout', {
      hotel_name: hotelName,
      step,
      room_type: roomType,
    })
  }

  const trackFormSubmit = (formName: string, extra?: EventParams) => {
    trackEvent('generate_lead', {
      form_name: formName,
      ...extra,
    })
  }

  const trackPurchase = (
    transactionId: string,
    hotelName: string,
    value: number,
    roomType: string
  ) => {
    trackEvent('purchase', {
      transaction_id: transactionId,
      hotel_name: hotelName,
      room_type: roomType,
      value,
      currency: 'USD',
    })
  }

  const trackBookingFail = (reason: string, hotelName: string, roomType?: string) => {
    trackEvent('booking_error', {
      reason,
      hotel_name: hotelName,
      room_type: roomType || 'not_specified',
    })
  }

  // === OTHER EVENTS ===
  const trackContactSubmit = (method: string) => {
    trackEvent('generate_lead', {
      method,
      page: currentPath,
    })
  }

  const trackBlogView = (blogTitle: string, category?: string) => {
    trackEvent('view_blog', {
      blog_title: blogTitle,
      category: category || 'uncategorized',
    })
  }

  const trackImageClick = (imageName: string, galleryName?: string) => {
    trackEvent('select_image', {
      image_name: imageName,
      gallery: galleryName || 'default',
    })
  }

  const trackScrollDepth = (percent: number) => {
    trackEvent('scroll', {
      percent_scrolled: percent,
      page_path: currentPath,
    })
  }

  const trackTimeOnPage = (seconds: number) =>
    trackEvent('time_on_page', { seconds, page: currentPath })

  return {
    // identity
    identifyUser,
    resetUser,
    // core
    trackEvent,
    trackPageView,

    // homepage
    trackHomepageBannerClick,
    trackCTAClick,

    // hotel
    trackHotelView,
    trackRoomView,
    trackBookingClick,
    trackStartCheckout,
    trackFormSubmit,
    trackPurchase,
    trackBookingFail,

    // others
    trackContactSubmit,
    trackBlogView,
    trackImageClick,
    trackScrollDepth,
    trackTimeOnPage,
  }
}
